/**
 * Task detail title (mahhp 22852) — declarative config + pure parsing helpers.
 *
 * `WS2_GS_SEL_TITRE_TACHE` (origami slug `boby-ws2-gs-sel-titre-tache`) returns
 * the two title lines as pre-formatted strings, e.g.
 *
 *   TITRE1: "B25H00903656 | Tache 183536 - Suivi courtier - Email - 02/02/2026"
 *   TITRE2: "*Suivi courtier* Cie_ Envoi de pieces | scenario : SUIVI"
 *
 * `TaskTitle` renders them on two lines: the claim reference (before the FIRST
 * ` | `) in monospace with muted separators on line 1, and the italic text plus
 * a "scenario : X" pill (from the trailing ` | scenario : X` segment) on line 2.
 * The parsing is deliberately forgiving — a missing segment just degrades to
 * plain text, never to an error.
 *
 * Lives in its own `.ts` module (not `TaskTitle.ts`) so it can never collide
 * with `TaskTitle.tsx` on a case-insensitive file system, and so the component
 * file only exports components (react-refresh).
 */

// ---------------------------------------------------------------------------
// Declarative config — stable keys, labels / aria labels out of the JSX
// ---------------------------------------------------------------------------

/** `{value}` is replaced by the parsed scenario (see {@link formatScenarioText}). */
export const SCENARIO_VALUE_PLACEHOLDER = '{value}';

export interface TaskTitleConfig {
  /** Accessible name of the two-line title group. */
  ariaLabel: string;
  /** Line 1 — the h1: claim reference + task summary. */
  line1: {
    key: 'line1';
    /** Muted separator rendered between the claim reference and the rest. */
    refSeparator: string;
    /** Muted separator rendered between the ` - ` segments of the rest. */
    segmentSeparator: string;
  };
  /** Line 2 — the italic task label. */
  line2: {
    key: 'line2';
    /** Muted separator rendered between the italic text and the scenario pill. */
    separator: string;
  };
  /** The scenario pill appended to line 2. */
  scenario: {
    key: 'scenario';
    /** Visible pill text template (`{value}` = scenario). */
    label: string;
    /** Accessible name / tooltip template of the pill (`{value}` = scenario). */
    ariaLabel: string;
  };
}

export const TASK_TITLE_CONFIG: TaskTitleConfig = {
  ariaLabel: 'Titre de la tâche',
  line1: {
    key: 'line1',
    refSeparator: '|',
    segmentSeparator: '-',
  },
  line2: {
    key: 'line2',
    separator: '|',
  },
  scenario: {
    key: 'scenario',
    label: `scenario : ${SCENARIO_VALUE_PLACEHOLDER}`,
    ariaLabel: `Scénario : ${SCENARIO_VALUE_PLACEHOLDER}`,
  },
};

/** Fills a `{value}` template from the config with the parsed scenario. */
export function formatScenarioText(template: string, scenario: string): string {
  return template.split(SCENARIO_VALUE_PLACEHOLDER).join(scenario);
}

// ---------------------------------------------------------------------------
// Parsing helpers
// ---------------------------------------------------------------------------

/** First ` | ` (whitespace around the pipe is optional). */
const REF_SEPARATOR = /\s*\|\s*/;

/**
 * ` - ` between two segments. Whitespace is REQUIRED on both sides so in-word
 * hyphens ("Jean-Pierre", "Cie_-x") are never split.
 */
const SEGMENT_SEPARATOR = /\s+-\s+/;

/**
 * Trailing ` | scenario : X` segment (case-insensitive). The value may not
 * contain a pipe, so only the LAST `|`-segment can match; an empty value is
 * captured too (and then dropped) so a dangling "| scenario :" never leaks
 * into the italic text.
 */
const SCENARIO_SUFFIX = /\s*\|\s*scenario\s*:([^|]*)$/i;

export interface Titre1Parts {
  /** Claim reference — the segment before the first ` | `; `null` when absent. */
  ref: string | null;
  /** Everything after the first ` | ` (the whole string when there is none). */
  rest: string;
}

/** Splits TITRE1 on its FIRST ` | ` into the claim reference and the rest. */
export function splitTitre1(titre1: string): Titre1Parts {
  const value = titre1.trim();
  const match = REF_SEPARATOR.exec(value);
  if (!match) return { ref: null, rest: value };

  const ref = value.slice(0, match.index).trim();
  const rest = value.slice(match.index + match[0].length).trim();
  return { ref: ref === '' ? null : ref, rest };
}

/** Splits the rest of TITRE1 on ` - ` (empty segments dropped). */
export function splitTitleSegments(rest: string): string[] {
  return rest
    .split(SEGMENT_SEPARATOR)
    .map((segment) => segment.trim())
    .filter((segment) => segment !== '');
}

export interface Titre2Parts {
  /** The italic text, without the trailing scenario segment. */
  text: string;
  /** The scenario value (e.g. "SUIVI"); `null` when the segment is absent or empty. */
  scenario: string | null;
}

/** Splits TITRE2 into its text and its trailing ` | scenario : X` value. */
export function splitTitre2(titre2: string): Titre2Parts {
  const value = titre2.trim();
  const match = SCENARIO_SUFFIX.exec(value);
  if (!match) return { text: value, scenario: null };

  const scenario = (match[1] ?? '').trim();
  return {
    text: value.slice(0, match.index).trim(),
    scenario: scenario === '' ? null : scenario,
  };
}
